import { useQuery } from '@tanstack/react-query';
import { FileIcon, FolderIcon, SearchIcon } from 'lucide-react';
import { useEffect, useState, type JSX } from 'react';

import { AppDialog } from '@/components/ui/app-dialog.js';
import { Button } from '@/components/ui/button.js';
import { FilePickerRow } from '@/components/ui/file-picker-row.js';
import { Input } from '@/components/ui/input.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs.js';
import { SharePointConnectionList } from '@/features/sharepoint/BrowseLevelList.js';
import { SHAREPOINT_PRESENCE_UNKNOWN } from '@/features/sharepoint/browse-error.js';
import { OneDriveTab } from '@/features/onedrive/OneDriveTab.js';
import { oneDriveAttachmentRef } from '@/features/onedrive/onedrive-attachment.js';
import type { SharePointConnectionChoice } from '@/features/sharepoint/connection-choice.js';
import { useSharePointKnowledgeMark } from '@/features/sharepoint/knowledge-mark.js';
import {
  NO_REMEMBERED_PATH,
  useRememberedPath,
} from '@/features/sharepoint/remembered-path.js';
import { SharePointBrowser } from '@/features/sharepoint/SharePointBrowser.js';
import { SharePointNotConnectedNotice } from '@/features/sharepoint/SharePointNotConnected.js';
import { SharePointSearchSurface } from '@/features/sharepoint/SharePointSearchSurface.js';
import {
  sharePointAttachmentRef,
  sharePointRefKey,
} from '@/features/sharepoint/sharepoint-attachment.js';
import type { SharePointRow } from '@/features/sharepoint/sharepoint-row.js';
import {
  SHAREPOINT_TREE_ROOT,
  type SharePointTreePosition,
} from '@/features/sharepoint/tree-position.js';
import { useCurrentWorkspaceRole } from '@/features/workspace/useCurrentWorkspaceRole.js';
import { useWorkspaceSharing } from '@/features/workspace/useWorkspaceSharing.js';
import { useConnectorPresence } from '@/integration/hooks/useConnections.js';
import type {
  ArtifactAttachmentRef,
  OneDriveAttachmentRef,
  SharePointAttachmentRef,
} from '@/integration/types.js';
import {
  workspaceArtifactsQueryOptions,
  type WorkspaceArtifact,
} from '@/lib/api/workspace-artifacts.js';

import {
  artifactAttachmentRef,
  restrictedArtifactIds,
} from './artifact-attachment.js';
import { formatBytes, typeLabel } from './artifact-display.js';

/** The two places a message can reference a file from, as tabs. */
type PickerTab = 'artifacts' | 'sharepoint' | 'onedrive';

/**
 * Picks files that already live in the workspace's world and attaches them to the message being composed.
 * No bytes move: an artifact travels as a reference pinned to its current revision, a SharePoint file as
 * its address, and the agent reads either where it already is.
 *
 * The two sources are TABS of one dialog rather than two menu items, because attaching a file is one job
 * and the user should not have to know which store it landed in first. That only works if both tabs pick
 * the same way: the row is the toggle, one Attach hands the whole mixed batch over, and the dialog closes.
 * Two interaction models for one job was what this replaced.
 *
 * **Picking and attaching are separate steps**, which is what makes several files one action. The
 * alternative — attach on click, reopen the picker for the next file — costs a round trip through the menu
 * per file and gives the composer no way to tell one batch from four.
 *
 * What a pick is held by differs, because the two lists lose a row for different reasons. Artifacts are
 * held by id and resolved against the live list, so a file someone deleted meanwhile leaves the batch
 * instead of becoming a reference to nothing, and the search box cannot silently drop a pick. SharePoint
 * files are held as the built address: walking into another folder — or searching, which replaces the whole
 * list — takes the row off screen, and a pick has to survive that. They are matched on the triple
 * `connection_id` + `drive_id` + `item_id`: a Graph `item_id` is unique only inside its own library and the
 * pair only inside one tenant, so two SharePoints can answer with the same pair for two documents.
 *
 * The SharePoint tab starts one level above the tree — WHICH SharePoint — because a workspace reaches more
 * than one, and a file in the caller's personal one has to be attachable from the place people attach
 * files. The rule is the one the Artifacts tab and the knowledge picker follow: a single connection is
 * taken silently, and the choice appears from two. Searching fans across every one of them from a box the
 * tab owns ({@link SharePointSearchSurface}), so a document is findable without knowing which SharePoint it
 * is in. A caller with no SharePoint connection of their own gets {@link SharePointNotConnectedNotice}
 * instead, and a failed presence read is reported rather than browsed around.
 *
 * Everything is dropped when the dialog closes — picks, search, which SharePoint and the tree's position.
 * The dialog stays mounted between visits, and a batch carried over from a message already sent is not what
 * the next one meant. The per-message cap belongs to the composer, not here: it is the one that knows what
 * is already attached, and it answers an over-cap batch with the same warning an over-cap upload gets.
 */
export function AddArtifactsDialog({
  workspaceId,
  open,
  onOpenChange,
  onAttachArtifacts,
  onAttachSharePointFiles,
  onAttachOneDriveFiles,
}: {
  workspaceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Receives the pinned descriptors of every picked artifact, and which of the picks are private. */
  onAttachArtifacts: (
    refs: ArtifactAttachmentRef[],
    restrictedIds: string[],
  ) => void;
  /**
   * Receives the address of every picked SharePoint file. Leaving it out hides the SharePoint tab
   * altogether, for a caller that has nowhere to put such a file — the automation window attaches
   * workspace files only. The composer always passes it, so chat keeps both tabs.
   */
  onAttachSharePointFiles?: (refs: SharePointAttachmentRef[]) => void;
  /** Receives personal OneDrive live refs. Conversation visibility is decided by the composer. */
  onAttachOneDriveFiles?: (refs: OneDriveAttachmentRef[]) => void;
}): JSX.Element {
  const [tab, setTab] = useState<PickerTab>('artifacts');
  const [search, setSearch] = useState('');
  /** Picked artifact ids in the order they were picked — that order is the order they become chips. */
  const [selectedArtifactIds, setSelectedArtifactIds] = useState<string[]>([]);
  const [sharePointPicks, setSharePointPicks] = useState<
    SharePointAttachmentRef[]
  >([]);
  const [position, setPosition] =
    useState<SharePointTreePosition>(SHAREPOINT_TREE_ROOT);
  /** Which SharePoint the tree belongs to. Component state, not a URL: this dialog has no address. */
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [oneDrivePosition, setOneDrivePosition] = useState({
    scopeId: null as string | null,
    folderId: null as string | null,
    itemId: null as string | null,
  });

  /** Whether this caller has anywhere to put a SharePoint file; nothing SharePoint-shaped is read without one. */
  const sharePointTab = onAttachSharePointFiles !== undefined;

  const query = useQuery({
    ...workspaceArtifactsQueryOptions(workspaceId),
    enabled: open,
  });
  const artifacts = query.data ?? [];
  /**
   * Which SharePoint rows are already on the workspace's knowledge shelf. It is only a hint here — this
   * dialog attaches files, it never shelves one — but it is the same fact the Artifacts section shows, so
   * it is read the same way and under the same role rule.
   */
  const role = useCurrentWorkspaceRole(workspaceId, open && sharePointTab);
  const knowledgeMark = useSharePointKnowledgeMark({
    workspaceId,
    role,
    enabled: open && sharePointTab,
  });
  /**
   * The SharePoints this caller reaches, read from presence and not from its flat `connected` field: the
   * flat one describes whichever connection the service resolved first, so a workspace reaching two looks
   * exactly like one reaching a single shared connection.
   */
  const presence = useConnectorPresence(workspaceId, open && sharePointTab);
  const sharing = useWorkspaceSharing(workspaceId);
  const connections: readonly SharePointConnectionChoice[] =
    presence.data?.providers.sharepoint?.connections ?? [];
  /** Whether presence has answered at all, with a list or with a failure. */
  const presenceRead = presence.data !== undefined || presence.isError;
  /**
   * The list could not be read (the fetch failed, or presence answered an error status with an empty
   * `readFailed` floor), so the tab says so and browses nothing: a listing that names no
   * connection is not an answer about this caller.
   */
  const presenceUnknown =
    (presence.data === undefined && presence.isError) ||
    presence.data?.readFailed === true;
  /** The caller holds no SharePoint connection of their own, so there is nothing for them to browse. */
  const notConnected =
    presence.data !== undefined && !presenceUnknown && connections.length === 0;
  const onlyConnection = connections.length === 1 ? connections[0]! : null;
  const connection =
    connections.find((candidate) => candidate.connectionId === connectionId) ??
    (connectionId === null ? onlyConnection : null);
  /**
   * The names of the levels walked through, held ABOVE the tree because a search unmounts it — state inside
   * it would die there, and clearing the box would put the user back on "Site / Library". Keyed by
   * connection, so a name read in one SharePoint never labels another's folder.
   */
  const rememberedPath = useRememberedPath(
    connection?.connectionId ?? 'resolved',
  );
  /**
   * The artifact picks that the list still has, in the order they were picked. Everything downstream reads
   * this and not `selectedArtifactIds`: a file someone else deleted while the dialog was open is still a
   * picked id, but attaching it is impossible, so counting it would let the button promise a file it then
   * drops — or stay enabled on a click that does nothing. One derived list, so the label, the disabled
   * state and the batch can never disagree.
   */
  const pickedArtifacts = selectedArtifactIds
    .map((artifactId) =>
      artifacts.find((artifact) => artifact.id === artifactId),
    )
    .filter(
      (artifact): artifact is WorkspaceArtifact => artifact !== undefined,
    );
  const pickedCount = pickedArtifacts.length + sharePointPicks.length;

  /**
   * One visit owns its batch, its search, which SharePoint it was in and where. The dialog outlives all of
   * them, and a batch carried over from a message already sent is not what the next one meant.
   */
  useEffect(() => {
    if (open) return;
    setTab('artifacts');
    setSearch('');
    setSelectedArtifactIds([]);
    setSharePointPicks([]);
    setPosition(SHAREPOINT_TREE_ROOT);
    setConnectionId(null);
  }, [open]);

  /**
   * The tab can be taken away under an open dialog — it hangs off the caller's callback, and the
   * automation window only passes one to a member who may add files, which is a role read that can answer
   * again. What is left behind is the state: the user stranded on a tab whose trigger is gone, and picks
   * that would ride into Attach unseen and be dropped there. Only the SharePoint half is cleared, and only
   * when it is actually gone, so the callers that always offer it — the composer, every chat — never
   * notice this effect exists.
   */
  useEffect(() => {
    if (sharePointTab) return;
    setTab((current) => (current === 'sharepoint' ? 'artifacts' : current));
    setSharePointPicks((current) => (current.length > 0 ? [] : current));
  }, [sharePointTab]);

  /**
   * Move to another SharePoint, or back to the choice. The position goes with it: a `drive_id` or an
   * `item_id` from one tenant addresses nothing in another, so keeping either would point the tree at ids
   * the new connection has never heard of. The walked names reset themselves — they are keyed by connection.
   */
  function changeConnection(next: string | null): void {
    setConnectionId(next);
    setPosition(SHAREPOINT_TREE_ROOT);
  }

  /**
   * A folder found by searching, which the user asked to open. It moves BOTH halves of the address at once —
   * which SharePoint and where inside it — because a hit can come from a connection other than the one on
   * screen, and moving the position alone would point the tree at another tenant's ids.
   */
  function openSearchHit(row: SharePointRow): void {
    if (row.treePosition === null) return;
    /** Nothing walked through leads to a hit, so the old names would label the new place. */
    rememberedPath[1](NO_REMEMBERED_PATH);
    setConnectionId(row.connectionId);
    setPosition(row.treePosition);
  }

  function toggleArtifact(artifactId: string): void {
    setSelectedArtifactIds((previous) =>
      previous.includes(artifactId)
        ? previous.filter((id) => id !== artifactId)
        : [...previous, artifactId],
    );
  }

  /**
   * The address a row travels as — built the one way, so the key a pick is matched on and the reference that
   * is attached can never disagree. It comes from the ROW, the connection included: a hit found by searching
   * knows which SharePoint answered it, and the tree the user is standing in does not.
   */
  function refFor(row: SharePointRow): SharePointAttachmentRef {
    return sharePointAttachmentRef({
      connectionId: row.connectionId,
      siteId: row.siteId,
      driveId: row.driveId,
      item: row.item,
    });
  }

  /**
   * Whether this row is already in the batch — the triple decides, not the item id and not the pair. The row
   * brings its own address, so a file found by searching is matched against picks made in the tree and the
   * other way round: the batch is one batch however each file was found.
   */
  function isSharePointFilePicked(row: SharePointRow): boolean {
    const key = sharePointRefKey(refFor(row));
    return sharePointPicks.some((pick) => sharePointRefKey(pick) === key);
  }

  function toggleSharePointFile(row: SharePointRow): void {
    const picked = refFor(row);
    const key = sharePointRefKey(picked);
    setSharePointPicks((previous) =>
      previous.some((pick) => sharePointRefKey(pick) === key)
        ? previous.filter((pick) => sharePointRefKey(pick) !== key)
        : [...previous, picked],
    );
  }

  /**
   * Hand the whole batch over as one gesture, whichever tabs it came from. The artifact descriptors are
   * built here, so each one pins the revision the file has at the moment of attaching; a file that left the
   * workspace while the dialog was open was already dropped by {@link pickedArtifacts} rather than attached
   * as a reference to nothing. Each carrier is written only when it has something to carry, so attaching
   * SharePoint files alone cannot clear an artifact batch the composer has not consumed yet.
   */
  function attachSelected(): void {
    if (pickedCount === 0) return;
    if (pickedArtifacts.length > 0) {
      onAttachArtifacts(
        pickedArtifacts.map((artifact) =>
          artifactAttachmentRef(workspaceId, artifact),
        ),
        restrictedArtifactIds(pickedArtifacts),
      );
    }
    if (sharePointPicks.length > 0) onAttachSharePointFiles?.(sharePointPicks);
    onOpenChange(false);
  }

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Add artifacts"
      description="Pick files from this workspace, SharePoint, or your personal OneDrive. Nothing is uploaded unless a shared conversation requires an explicit OneDrive copy."
    >
      <Tabs value={tab} onValueChange={(value) => setTab(value as PickerTab)}>
        <TabsList>
          <TabsTrigger value="artifacts">
            <FileIcon /> Artifacts
          </TabsTrigger>
          {sharePointTab && (
            <TabsTrigger value="sharepoint">
              <FolderIcon /> SharePoint
            </TabsTrigger>
          )}
          {onAttachOneDriveFiles && (
            <TabsTrigger value="onedrive">
              <FolderIcon /> OneDrive
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="artifacts">
          <ArtifactsPanel
            artifacts={artifacts}
            isLoading={query.isLoading}
            isError={query.isError}
            search={search}
            onSearchChange={setSearch}
            selectedIds={selectedArtifactIds}
            onToggle={toggleArtifact}
          />
        </TabsContent>

        <TabsContent value="sharepoint">
          {!presenceRead && <Skeleton className="h-10 w-full rounded-md" />}

          {notConnected && (
            <SharePointNotConnectedNotice workspaceId={workspaceId} />
          )}

          {presenceUnknown && (
            <p role="alert" className="text-sm text-destructive">
              {SHAREPOINT_PRESENCE_UNKNOWN}
            </p>
          )}

          {presence.data !== undefined && !presenceUnknown && !notConnected && (
            <div className="flex min-w-0 flex-col gap-3">
              {connection !== null && connections.length > 1 && (
                <div className="flex justify-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => changeConnection(null)}
                  >
                    Change SharePoint
                  </Button>
                </div>
              )}
              <SharePointSearchSurface
                workspaceId={workspaceId}
                connections={connections}
                onOpenFolder={openSearchHit}
                knowledgeMark={knowledgeMark}
                fileSelection={{
                  isSelected: isSharePointFilePicked,
                  toggle: toggleSharePointFile,
                }}
              >
                <div className="max-h-96 overflow-y-auto">
                  {connection !== null ? (
                    <SharePointBrowser
                      /**
                       * Keyed by connection: everything this tree holds was read in one SharePoint, and a
                       * Graph id means nothing in another tenant.
                       */
                      key={connection.connectionId}
                      workspaceId={workspaceId}
                      connectionId={connection.connectionId}
                      connectionLabel={connection.label}
                      position={position}
                      onNavigate={setPosition}
                      hostOwnsSearch
                      rememberedPath={rememberedPath}
                      knowledgeMark={knowledgeMark}
                      fileSelection={{
                        isSelected: isSharePointFilePicked,
                        toggle: toggleSharePointFile,
                      }}
                    />
                  ) : (
                    <div className="flex min-w-0 flex-col gap-1 rounded-xl border border-border p-2">
                      <SharePointConnectionList
                        connections={connections}
                        sharing={sharing}
                        onPick={(picked) =>
                          changeConnection(picked.connectionId)
                        }
                      />
                    </div>
                  )}
                </div>
              </SharePointSearchSurface>
            </div>
          )}
        </TabsContent>

        {onAttachOneDriveFiles && (
          <TabsContent value="onedrive">
            <div className="max-h-[32rem] overflow-y-auto">
              <OneDriveTab
                workspaceId={workspaceId}
                scopeId={oneDrivePosition.scopeId}
                folderId={oneDrivePosition.folderId}
                itemId={oneDrivePosition.itemId}
                onNavigate={(scopeId, folderId, itemId) =>
                  setOneDrivePosition({ scopeId, folderId, itemId })
                }
                onAddToChat={(scopeId, item) => {
                  onAttachOneDriveFiles([oneDriveAttachmentRef(scopeId, item)]);
                  onOpenChange(false);
                }}
              />
            </div>
          </TabsContent>
        )}
      </Tabs>

      <div className="mt-3 flex items-center justify-end gap-2 border-t border-border pt-3">
        <Button variant="ghost" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        {tab !== 'onedrive' && (
          <Button
            variant="brand"
            onClick={attachSelected}
            disabled={pickedCount === 0}
          >
            {pickedCount === 0
              ? 'Attach'
              : `Attach ${pickedCount} file${pickedCount === 1 ? '' : 's'}`}
          </Button>
        )}
      </div>
    </AppDialog>
  );
}

/**
 * The workspace's own files, searchable by name. The list is exactly what the registry answered — the
 * service already hides files this caller may not see, so there is nothing to filter for access here (and a
 * client-side "hide it anyway" pass would only be able to make the list wrong).
 */
function ArtifactsPanel({
  artifacts,
  isLoading,
  isError,
  search,
  onSearchChange,
  selectedIds,
  onToggle,
}: {
  artifacts: WorkspaceArtifact[];
  isLoading: boolean;
  isError: boolean;
  search: string;
  onSearchChange: (search: string) => void;
  selectedIds: string[];
  onToggle: (artifactId: string) => void;
}): JSX.Element {
  const normalizedSearch = search.trim().toLowerCase();
  const visibleArtifacts = normalizedSearch
    ? artifacts.filter((artifact) =>
        artifact.name.toLowerCase().includes(normalizedSearch),
      )
    : artifacts;

  return (
    <>
      <div className="relative">
        <SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Search files by name"
          className="pl-9"
          aria-label="Search workspace files"
        />
      </div>

      <div className="mt-3 max-h-80 overflow-y-auto">
        {isLoading ? (
          <div className="flex flex-col gap-2">
            {['a', 'b', 'c'].map((key) => (
              <Skeleton key={key} className="h-11 w-full rounded-lg" />
            ))}
          </div>
        ) : isError ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            Files are unavailable right now. Try again shortly.
          </p>
        ) : visibleArtifacts.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {artifacts.length === 0
              ? 'No files in this workspace yet.'
              : 'No files match your search.'}
          </p>
        ) : (
          <ul className="flex flex-col gap-1">
            {visibleArtifacts.map((artifact) => (
              <li key={artifact.id}>
                <FilePickerRow
                  name={artifact.name}
                  meta={`${typeLabel(artifact.contentType, artifact.name)} · ${formatBytes(artifact.size)}`}
                  selected={selectedIds.includes(artifact.id)}
                  onToggle={() => onToggle(artifact.id)}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}
